import { useEffect, useState } from "react";
import useCrmApi from "../../../components/hook/crm-req";

// Data warehouse traits integration — see
// docs/data-warehouse-traits-integration.md. Additive alongside the existing
// mock /traits, /traits/overview, /traits/customers (../api.ts) — those stay
// the default experience; this is read-only, opt-in "Live Data" content
// scoped server-side to the caller's tenant (never a client-supplied param).
// No create/update/delete/migrate endpoint exists here.

export interface WarehouseStatus {
  enabled: boolean;
  configured: boolean;
  schema: string;
  mode: string;
}

export interface WarehouseTraitDefinition {
  trait_name: string;
  // `category` (custom|computed|predictive) and `warehouse_trait_type` are
  // the confirmed live shape for a category-scoped catalog call; `trait_type`
  // is kept for the older unscoped call site (all-traits/index.tsx) in case
  // that's still what it returns there.
  category?: "custom" | "computed" | "predictive";
  trait_type?: string;
  warehouse_trait_type?: string;
  data_type: string;
  description?: string;
  computed_from?: string;
  example_value: unknown;
  profile_count: number;
  coverage: number;
  last_computed_at: string;
}

export interface WarehouseCatalogResponse {
  data: WarehouseTraitDefinition[];
  total: number;
  source: "data_warehouse";
}

export interface WarehouseCatalogParams {
  category?: "custom" | "computed" | "predictive";
  search?: string;
  page?: number;
  per_page?: number;
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

// ─── Status — gates whether any "Live Data" toggle is offered at all ──────
export function useWarehouseStatus() {
  const { makeRequest } = useCrmApi("/traits/warehouse/status", "GET", authHeaders());
  const [data, setData] = useState<WarehouseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      setLoading(false);
      setData(status >= 200 && status < 300 ? json : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading };
}

// ─── Catalog — GET /traits/warehouse/catalog?category=&search=&page=&per_page= ──
// `category`/`search`/`page`/`per_page` are all optional; omitting them
// returns the same flat, unscoped list the original version of this hook did
// (all-traits/index.tsx's Live Data table still calls it that way).
export function useWarehouseCatalog(enabled: boolean, params?: WarehouseCatalogParams) {
  const { makeRequest } = useCrmApi("/traits/warehouse/catalog", "GET", authHeaders());
  const [data, setData] = useState<WarehouseCatalogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      // Only send keys that are actually set — an explicit `undefined` value
      // gets stringified as the literal text "undefined" by URLSearchParams
      // rather than omitted.
      const query: Record<string, string | number> = {};
      if (params?.category) query.category = params.category;
      if (params?.search) query.search = params.search;
      if (params?.page) query.page = params.page;
      if (params?.per_page) query.per_page = params.per_page;
      const [json, status] = await makeRequest(undefined, Object.keys(query).length ? query : undefined);
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(json);
      else setError("Couldn't load the warehouse catalog.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, paramsKey]);

  return { data, loading, error };
}

export function formatWarehouseTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function formatWarehouseValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
